import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { comPulseira } from "@/features/cliente-sessao";

const API = '/api';

// Validação de CPF
function validateCPF(cpf) {
  // Remove formatação
  cpf = cpf.replace(/[^\d]/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  // Valida primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;
  
  // Valida segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;
  
  return true;
}

// Formatar CPF com máscara progressiva (xxx.xxx.xxx-xx)
function formatCPF(value) {
  // Remove tudo que não é número
  let cpf = value.replace(/\D/g, '');

  // Limita a 11 dígitos
  cpf = cpf.slice(0, 11);

  // Aplica a máscara progressivamente
  if (cpf.length > 9) {
    // xxx.xxx.xxx-xx
    cpf = cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  } else if (cpf.length > 6) {
    // xxx.xxx.xxx
    cpf = cpf.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
  } else if (cpf.length > 3) {
    // xxx.xxx
    cpf = cpf.replace(/(\d{3})(\d{1,3})/, '$1.$2');
  }

  return cpf;
}

export default function CPFModal({ open, onClose, customerId, onSaved }) {
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!cpf) {
      setError('CPF é obrigatório');
      return;
    }
    
    const cpfClean = cpf.replace(/[^\d]/g, '');
    
    if (!validateCPF(cpfClean)) {
      setError('CPF inválido. Verifique os dígitos.');
      return;
    }
    
    setLoading(true);
    try {
      // Pulseira do cliente no cardápio; no Totem vai o login da loja (achado 114)
      await axios.post(
        `${API}/customers/${customerId}/cpf`,
        { cpf: cpfClean },
        comPulseira()
      );
      
      toast.success("CPF salvo com sucesso!");
      if (onSaved) {
        onSaved();
      }
      setCpf('');
      onClose();
    } catch (error) {
      setError(error.response?.data?.detail || "Erro ao salvar CPF");
    } finally {
      setLoading(false);
    }
  };

  const handleCpfChange = (e) => {
    const value = e.target.value;
    const formatted = formatCPF(value);
    setCpf(formatted);
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>CPF necessário para PIX Online</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="cpf">CPF</Label>
            <Input
              id="cpf"
              type="text"
              value={cpf}
              onChange={handleCpfChange}
              placeholder="000.000.000-00"
              maxLength={14}
              className={`h-12 text-lg ${error ? 'border-red-500' : ''}`}
            />
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm mt-1">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              O CPF é necessário para processar pagamentos via PIX Online
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-12"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12"
            >
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
